import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationDelegationsApi, settingsApi } from "../api/settings";
import { broadcastRefreshRequest } from "@/features/tasks/hooks/useNotificationSync";
import { useAuthStore, type TenderBadgePreference } from "@/store/auth";

/** Uprawnienia z dodatkowych flag, które zalogowany ma (także z funkcji).
 *  `enabled` — sekcja pyta dopiero po rozwinięciu, zwinięta nic nie pokazuje. */
export function useMyPermissions(enabled = true) {
  const userId = useAuthStore((s) => s.user?.id);
  return useQuery({
    queryKey: ["auth", "me", "permissions", userId],
    queryFn: settingsApi.myPermissions,
    enabled: enabled && !!userId,
  });
}

export function useUpdateTenderBadgePreferences() {
  return useMutation({
    mutationFn: (data: Partial<TenderBadgePreference>) =>
      settingsApi.updateTenderBadgePreferences(data),
    onSuccess: (pref) => {
      const { user, updateUser } = useAuthStore.getState();
      if (user) updateUser({ ...user, tender_badge_preference: pref });
    },
  });
}

export function useUpdateProfile() {
  const updateUser = useAuthStore((s) => s.updateUser);
  return useMutation({
    mutationFn: (data: {
      phone_number?: string;
      email?: string;
      receives_print_order_notifications?: boolean;
    }) => settingsApi.updateProfile(data),
    onSuccess: (updatedUser) => updateUser(updatedUser),
  });
}

export function useUploadAvatar() {
  const updateUser = useAuthStore((s) => s.updateUser);
  return useMutation({
    mutationFn: (blob: Blob) => settingsApi.uploadAvatar(blob),
    onSuccess: (updatedUser) => updateUser(updatedUser),
  });
}

export function useDeleteAvatar() {
  const updateUser = useAuthStore((s) => s.updateUser);
  return useMutation({
    mutationFn: settingsApi.deleteAvatar,
    onSuccess: (updatedUser) => updateUser(updatedUser),
  });
}

// ── Przekazywanie powiadomień ────────────────────────────────────────────────

export const NOTIFICATION_DELEGATIONS_KEY = ["notification-delegations"] as const;

export function useNotificationDelegations(enabled = true) {
  return useQuery({
    queryKey: NOTIFICATION_DELEGATIONS_KEY,
    queryFn: notificationDelegationsApi.state,
    enabled,
    staleTime: 30_000,
  });
}

/** The picker's options — fetched only while the picker is actually shown. */
export function useDelegationCandidates(enabled: boolean) {
  return useQuery({
    queryKey: [...NOTIFICATION_DELEGATIONS_KEY, "candidates"],
    queryFn: notificationDelegationsApi.candidates,
    enabled,
  });
}

export function useDelegationMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: NOTIFICATION_DELEGATIONS_KEY });
  return {
    create: useMutation({
      mutationFn: (input: { delegate: string; sync_read: boolean }) =>
        notificationDelegationsApi.create(input),
      onSuccess: refresh,
    }),
    setSyncRead: useMutation({
      mutationFn: ({ id, sync_read }: { id: string; sync_read: boolean }) =>
        notificationDelegationsApi.setSyncRead(id, sync_read),
      onSuccess: refresh,
    }),
    accept: useMutation({ mutationFn: notificationDelegationsApi.accept, onSuccess: refresh }),
    decline: useMutation({ mutationFn: notificationDelegationsApi.decline, onSuccess: refresh }),
    revoke: useMutation({
      mutationFn: notificationDelegationsApi.revoke,
      onSuccess: () => {
        refresh();
        // Ending a takeover marks the delegate's unread copies read on the server —
        // refresh the bell now instead of at the next 30 s poll.
        queryClient.invalidateQueries({ queryKey: ["task-notifications"] });
        broadcastRefreshRequest();
      },
    }),
  };
}
